import { Button, Grid, InputLabel, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import TimePicker from "react-time-picker";
import * as Yup from "yup";
import { ApiError, ApiErrorClass, Exercise } from "@readyfit-common/models";
import {
  convertMinuteStringToTotalMinutes,
  convertTotalMinutesToMinutesString,
  handleApiError,
} from "@readyfit-common/utils";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import { StyledTimePickerWrapper } from "src/grading/components/WorkoutForm/FitnessWorkoutForm";
import { useAppDispatch } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  SaveToExerciseLibraryRequest,
  useDeleteExerciseLibraryMutation,
  useSaveToExerciseLibraryMutation,
  useUpdateExerciseLibraryMutation,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import useStyles from "./ExerciseModal.style";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

interface ExerciseModalProps {
  open: boolean;
  onClose: () => void;
  isEditing?: boolean;
  exercise: Exercise | null;
}

export interface ExerciseModalValues {
  name: string;
  sets: string;
  reps: string;
  weight: string;
  time: string;
  description: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  sets: Yup.string().required("Sets is required"),
  reps: Yup.string().required("Reps is required"),
  weight: Yup.string().required("Weight is required"),
  time: Yup.string().required("Time is required"),
  description: Yup.string().required("Description is required"),
});

const ExerciseModal = ({
  open,
  onClose,
  isEditing,
  exercise,
}: ExerciseModalProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [saveToExerciseLibrary, { isLoading }] =
    useSaveToExerciseLibraryMutation();
  const [updateExerciseLibrary] = useUpdateExerciseLibraryMutation();
  const [deleteExerciseLibrary] = useDeleteExerciseLibraryMutation();

  const formik = useFormik({
    initialValues: {
      name: exercise?.name || "",
      sets: exercise?.sets ? String(exercise?.sets) : "",
      reps: exercise?.reps ? String(exercise?.reps) : "",
      weight: exercise?.weight || "",
      time: exercise?.time
        ? convertTotalMinutesToMinutesString(exercise.time)
        : "00:00",
      description: exercise?.description || "",
    },
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values: ExerciseModalValues) => {
      try {
        // send only updated fields
        const updatedFields = Object.keys(values).reduce((acc, key) => {
          if (
            values[key as keyof ExerciseModalValues] !==
            exercise?.[key as keyof Exercise]
          ) {
            acc[key as keyof ExerciseModalValues] =
              values[key as keyof ExerciseModalValues];
          }
          return acc;
        }, {} as ExerciseModalValues);

        const exercisePayload: SaveToExerciseLibraryRequest = {
          ...updatedFields,
          time: convertMinuteStringToTotalMinutes(updatedFields.time),
          sets: values.sets ? Number(values.sets) : 0,
          reps: values.reps ? Number(values.reps) : 0,
        };

        if (isEditing && exercise) {
          await updateExerciseLibrary({
            ...exercisePayload,
            id: exercise.id,
          }).unwrap();
        } else {
          await saveToExerciseLibrary(exercisePayload).unwrap();
        }

        dispatch(
          showSnackbar({
            open: true,
            message: `Exercise ${isEditing ? "updated" : "saved"} successfully`,
          }),
        );
        handleClose();
      } catch (error: unknown) {
        const typedError = error as ApiError;
        handleApiError(dispatch, typedError);
      }
    },
  });

  const handleDelete = async () => {
    try {
      if (!exercise?.id) {
        throw new ApiErrorClass({
          message: "Exercise ID is required",
        });
      }
      await deleteExerciseLibrary({ id: exercise?.id }).unwrap();
      dispatch(
        showSnackbar({
          open: true,
          message: "Exercise deleted successfully",
        }),
      );
      handleClose();
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    handleChange,
    resetForm,
  } = formik;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleNumberChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
  ) => {
    const value = event.target.value;
    // Only update if the value is a valid number or empty
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setFieldValue(field, value);
    }
  };

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={handleClose}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        rowGap={theme.spacing(3)}
      >
        <Typography variant="h5" fontSize={18}>
          {isEditing ? "Edit Exercise" : "Add Exercise"}
        </Typography>

        <Grid
          container
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }}
          rowGap={theme.spacing(3)}
          spacing={2}
        >
          <Grid item xs={12}>
            <InputLabel htmlFor="name" className={classes.inputLabel}>
              Name
            </InputLabel>
            <TextField
              fullWidth
              variant="outlined"
              value={values.name}
              placeholder="Name"
              error={!!errors.name}
              helperText={errors.name}
              InputProps={{
                className: classes.input,
              }}
              onChange={handleChange("name")}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel htmlFor="sets" className={classes.inputLabel}>
              Sets
            </InputLabel>
            <TextField
              fullWidth
              variant="outlined"
              value={values.sets}
              placeholder="Sets"
              error={!!errors.sets}
              helperText={errors.sets}
              InputProps={{
                className: classes.input,
              }}
              onChange={(e) => handleNumberChange(e, "sets")}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel htmlFor="reps" className={classes.inputLabel}>
              Reps
            </InputLabel>
            <TextField
              fullWidth
              variant="outlined"
              value={values.reps}
              placeholder="Reps"
              error={!!errors.reps}
              helperText={errors.reps}
              InputProps={{
                className: classes.input,
              }}
              onChange={(e) => handleNumberChange(e, "reps")}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel htmlFor="weight" className={classes.inputLabel}>
              Weight
            </InputLabel>
            <TextField
              fullWidth
              variant="outlined"
              value={values.weight}
              placeholder="Weight"
              error={!!errors.weight}
              helperText={errors.weight}
              InputProps={{
                className: classes.input,
              }}
              onChange={(e) => handleNumberChange(e, "weight")}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel htmlFor="estimatedTime" className={classes.inputLabel}>
              <Typography variant="subtitle1">Estimated Time</Typography>
            </InputLabel>
            <StyledTimePickerWrapper>
              <TimePicker
                onChange={(value) => {
                  if (value) {
                    setFieldValue("time", value.toString());
                  }
                }}
                value={values.time || "00:00"}
                format="HH:mm"
                clearIcon={null}
                disableClock={true}
              />
            </StyledTimePickerWrapper>
            {errors.time && (
              <Typography variant="caption" color="error">
                {errors.time}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="description" className={classes.inputLabel}>
              Description
            </InputLabel>
            <TextField
              multiline
              rows={2}
              fullWidth
              variant="outlined"
              value={values.description}
              placeholder="Description"
              error={!!errors.description}
              helperText={errors.description}
              InputProps={{
                className: classes.input,
              }}
              onChange={handleChange("description")}
            />
          </Grid>
        </Grid>

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          marginTop={theme.spacing(4)}
        >
          <Grid item>
            <Button
              className={` ${classes.button} ${classes.deleteButton}`}
              onClick={() => {
                if (isEditing) {
                  handleDelete();
                } else {
                  onClose();
                }
              }}
              variant="contained"
            >
              {isEditing ? "Delete" : "Cancel"}
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label="Save"
              onClick={() => handleSubmit()}
              loading={isLoading}
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default ExerciseModal;
